import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth, useIsAdmin } from '../auth/AuthContext';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import BlogMarkdown from '../lib/BlogMarkdown';
import {
  getPublishedBlog, getBlogForEdit, deleteBlog, type BlogPost,
} from '../lib/blogsApi';
import {
  linkedInShareUrl, xShareUrl, facebookShareUrl, copyBlogLink,
} from '../lib/blogShare';
import './Blog.css';

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

// Full post reader. Works logged-out for published posts; admins can also read
// drafts (via the admin route) and get Edit/Delete plus a draft banner.
const BlogPostPage: React.FC = () => {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { state } = useAuth();
  const isAdmin = useIsAdmin();
  const token = state.kind === 'authenticated' ? state.idToken : undefined;

  const [post, setPost] = useState<BlogPost | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  useDocumentTitle(post ? post.title : 'blog');

  useEffect(() => {
    let cancelled = false;
    setPost(null); setError(null);
    (async () => {
      try {
        // Admins try the admin route first so drafts are readable; fall back to
        // the public route (e.g. token expired) so published posts still load.
        let data: BlogPost;
        if (isAdmin) {
          try { data = await getBlogForEdit(slug, token); }
          catch { data = await getPublishedBlog(slug); }
        } else {
          data = await getPublishedBlog(slug);
        }
        if (!cancelled) setPost(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Post not found.');
      }
    })();
    return () => { cancelled = true; };
  }, [slug, isAdmin, token]);

  const onDelete = async () => {
    if (!window.confirm(`Delete post "${post?.title}"? This is permanent.`)) return;
    try {
      await deleteBlog(slug, token);
      navigate('/blogs');
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Delete failed.');
    }
  };

  if (error) {
    return (
      <div className="blog-page">
        <div className="blog-error">{error}</div>
        <Link to="/blogs" className="blog-back">← all posts</Link>
      </div>
    );
  }
  if (!post) return <div className="blog-page blog-muted">Loading…</div>;

  return (
    <article className="blog-page blog-post">
      <Link to="/blogs" className="blog-back">← all posts</Link>

      {post.status === 'draft' && <div className="blog-draft-banner">This is a draft — only admins can see it.</div>}

      {post.cover_image_url && <img className="blog-post-cover" src={post.cover_image_url} alt="" />}
      {post.tags && post.tags.length > 0 && (
        <div className="blog-card-topics">
          {post.tags.map(t => <span key={t} className="blog-topic-chip">{t}</span>)}
        </div>
      )}
      <h1 className="blog-post-title">{post.title}</h1>
      <div className="blog-post-meta">
        {post.author_handle && <span>by {post.author_handle}</span>}
        <span>{formatDate(post.published_at || post.created_at)}</span>
        {post.read_minutes > 0 && <span>{post.read_minutes} min read</span>}
        {isAdmin && (
          <span className="blog-post-admin">
            <Link to={`/blogs/${slug}/edit`} className="blog-link">edit</Link>
            <button className="blog-link danger" onClick={onDelete}>delete</button>
          </span>
        )}
      </div>

      <BlogMarkdown text={post.body_markdown} />

      <div className="blog-share">
        <span className="blog-share-label">share</span>
        <a className="blog-share-btn" href={linkedInShareUrl(slug)} target="_blank" rel="noopener noreferrer">LinkedIn</a>
        <a className="blog-share-btn" href={xShareUrl(slug, post.title)} target="_blank" rel="noopener noreferrer">X</a>
        <a className="blog-share-btn" href={facebookShareUrl(slug)} target="_blank" rel="noopener noreferrer">Facebook</a>
        <button className="blog-share-btn" onClick={async () => setCopied(await copyBlogLink(slug))}>
          {copied ? 'copied!' : 'copy link'}
        </button>
      </div>
    </article>
  );
};

export default BlogPostPage;
